import React, { useContext, useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import DocumentMeta from "react-document-meta";
import axios from "axios";
import HeaderBanner from "../Components/HeaderBanner";
import VersionSideBar from "../Components/VersionSideBar";
import Config from "../config.json";
import Loading from "./Loading";
import ComponentContext from "../Context";
import Noskheh from "../Images/noskheh.png";
import Statics from "../Statics";

const title = "مشخصات نسخه‌ها";

const page_meta = {
  language: "fa",
  copyright: "پژوهیار",
  description: "نرم‌افزار استناددهی",
  title: Config.title + " | مشخصات نسخه‌ها",
  meta: {
    charset: "utf-8",
    name: {
      keywords: "پژوهیار",
    },
  },
};

const body =
  "اطلاعات مربوط به نسخه‌های پژوهیار ( نسخه 1، نسخه 1.5، نسخه 2،نسخه 3) برای هر یک از نسخه‌های منتشرشده در دسترس کاربران قرار می‌گیرد. مشخصات نسخه‌ها به هنگام به‌روزرسانی منتشر می‌شود.";

const parseNumericPriority = value => {
  if (value === null || typeof value === "undefined" || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseVersionSegments = value =>
  String(value || "")
    .split(".")
    .map(segment => Number(segment))
    .filter(segment => !Number.isNaN(segment));

const compareVersionSegmentsDesc = (left, right) => {
  const leftSegments = parseVersionSegments(left);
  const rightSegments = parseVersionSegments(right);
  const maxLength = Math.max(leftSegments.length, rightSegments.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftValue = leftSegments[index] ?? 0;
    const rightValue = rightSegments[index] ?? 0;

    if (leftValue !== rightValue) {
      return rightValue - leftValue;
    }
  }

  return 0;
};

const comparePriorityThenVersion = (left, right) => {
  const leftPriority = parseNumericPriority(left?.priority);
  const rightPriority = parseNumericPriority(right?.priority);

  if (leftPriority !== null || rightPriority !== null) {
    if (leftPriority === null) return 1;
    if (rightPriority === null) return -1;
    if (leftPriority !== rightPriority) return leftPriority - rightPriority;
  }

  const versionComparison = compareVersionSegmentsDesc(
    left?.version,
    right?.version,
  );
  if (versionComparison !== 0) {
    return versionComparison;
  }

  return String(right?.updated_at || "").localeCompare(
    String(left?.updated_at || ""),
  );
};

const mapVersionsToChangelogs = rawVersions =>
  (Array.isArray(rawVersions) ? [...rawVersions] : [])
    .sort(comparePriorityThenVersion)
    .map(version => ({
      value:
        version?.title ||
        (version?.version ? `نسخه ${version.version}` : "نسخه نامشخص"),
      children: (Array.isArray(version?.notes) ? [...version.notes] : [])
        .sort(comparePriorityThenVersion)
        .map(note => ({
          title:
            note?.revision_label ||
            (note?.version ? `نگارش ${note.version}` : note?.title || "نگارش"),
          body: note?.content || "",
          version: note?.version || "",
        })),
    }));

const Versions = () => {
  const context = useContext(ComponentContext);

  const [loadingState, setLoadingState] = useState(Statics.NOT_LOADED);
  const [changelogs, setChangelogs] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);

  const fetchChangelogsFromServer = () => {
    axios
      .get(`${context.ApiDriver.config.url}/versions`, {
        params: { with_notes: 1, _ts: Date.now() },
      })
      .then(({ data }) => {
        const normalized = mapVersionsToChangelogs(data?.data);
        setChangelogs(normalized);
        setLoadingState(Statics.LOADED);
      })
      .catch(error => {
        console.log(error);
        if (changelogs.length === 0) {
          setLoadingState(Statics.CAUSED_ERROR);
        }
      });
  };

  const getChangelogs = () => {
    setLoadingState(Statics.LOADING);
    fetchChangelogsFromServer();
  };
  let lastVersion;
  let lastPublication;
  if (changelogs[0] && changelogs[0].children.length > 0) {
    lastVersion = changelogs[0].value;
    lastPublication = changelogs[0].children[0];
  } else {
    lastVersion = "نسخه‌ای ثبت نشده";
    lastPublication = {
      title: "محتوایی برای نمایش وجود ندارد",
      body: "در حال حاضر داده‌ای برای «مشخصات نسخه‌ها» در هسته ثبت نشده است.",
    };
  }

  useEffect(() => {
    if (loadingState === Statics.NOT_LOADED) {
      getChangelogs();
    }
    // eslint-disable-next-line
  }, [loadingState]);

  useEffect(() => {
    if (!selectedChild) return;

    const refreshedSelectedChild = changelogs
      .flatMap(group =>
        (group?.children || []).map(child => ({
          ...child,
          parentLink: { value: group?.value || "" },
        })),
      )
      .find(
        child =>
          child?.title === selectedChild?.title &&
          child?.body === selectedChild?.body,
      );

    if (!refreshedSelectedChild) {
      setSelectedChild(null);
      return;
    }

    const hasChangedParent =
      selectedChild?.parentLink?.value !==
      refreshedSelectedChild?.parentLink?.value;
    if (hasChangedParent) {
      setSelectedChild(refreshedSelectedChild);
    }
    // eslint-disable-next-line
  }, [changelogs]);

  const handleChildSelect = child => {
    setSelectedChild(child);
  };
  return (
    <DocumentMeta {...page_meta}>
      <HeaderBanner
        image={Noskheh}
        body={body}
        title={title}
        header_color="#F2E2B1"
      />
      <Row className="p-4 p-md-5 mx-0" style={{ background: "#fff" }}>
        {loadingState === Statics.LOADING ? (
          <div className="pajoohyar-loading">
            <Loading />
          </div>
        ) : null}
        {loadingState === Statics.CAUSED_ERROR ? (
          <div className="version-error-parent">
            <p>ارتباط با سرور با مشکل مواجه شد !</p>
            <img
              className="version-error-img"
              src={`${process.env.PUBLIC_URL}/images/errorPic.webp`}
              alt="error-png"
            />
          </div>
        ) : null}
        <Col md={3} className="mb-3 mb-md-0 p-0 pl-0 pl-md-3">
          {loadingState === Statics.LOADED ? (
            <VersionSideBar
              links={changelogs}
              sidebar_title="نسخه‌ها"
              onChildSelect={handleChildSelect}
            />
          ) : null}
        </Col>

        {loadingState === Statics.LOADED ? (
          <Col md={9}>
            <Row className="container-content-with-sidebar">
              <Col>
                {loadingState === Statics.LOADED && selectedChild ? (
                  <div>
                    <h5 className="version-title">
                      {selectedChild.parentLink.value}
                      {" > "}
                      {selectedChild.title}
                    </h5>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: selectedChild.body,
                      }}
                      className="version-item-content text-justify card-body"
                    />{" "}
                  </div>
                ) : null}
                {loadingState === Statics.LOADED && !selectedChild ? (
                  <div>
                    <h5 className="version-title">
                      {lastVersion}
                      {" > "}
                      {lastPublication.title}
                    </h5>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: lastPublication.body,
                      }}
                      className="version-item-content text-justify card-body"
                    />
                  </div>
                ) : null}
              </Col>
            </Row>
          </Col>
        ) : null}
      </Row>
    </DocumentMeta>
  );
};

export default Versions;
